"use client";

import Link from "next/link";
import { useState } from "react";
import { CopyButton } from "./interactive";
import type { CatalogModel } from "@/lib/dashboard/models";
import { PUBLIC_API_BASE_URL } from "@/lib/api/public-url";
import { cn } from "@/lib/utils";

/**
 * Language-tabbed code snippets with copy: the "grab a snippet, get a response"
 * surface of the model drawer. P/9 is OpenAI-compatible, so the snippets use the
 * OpenAI SDKs pointed at the P/9 base URL, with the row's real route id baked in.
 *
 * Removed with the mock catalogue in the pre-launch honesty pass, revived
 * 2026-08-15 over `CatalogModel`. Everything a snippet claims is verifiable:
 * the base URL is the live gateway, the id is the route the backend serves, and
 * the key comes from the reader's own environment. The one thing the old
 * version did that this one must not is mint a fake key to paste; keys come
 * from the API keys page, which the footer points at.
 *
 * **Both the host and the key are environment variables** (2026-09-01). The
 * host used to be inlined into every snippet, which meant a reader who copied
 * the cURL tab, then the Python tab, then a snippet off the BYOK page, pasted
 * the same literal three times and had three places to edit when pointing at
 * anything else. One name, `P9_BASE_URL`, now appears in every snippet on
 * every page, alongside `P9_API_KEY`.
 *
 * `P9_BASE_URL` is the **host root**, not the `/v1` root, and the `/v1` is
 * written into each snippet instead. That is the only value that works for
 * both clients: an OpenAI SDK's `baseURL` includes `/v1` and the Anthropic
 * SDK's `base_url` must not, because it appends `/v1/messages` itself. Storing
 * the `/v1` form would force one of the two to slice it back off, which is a
 * worse thing to ask of a reader than an explicit path in the snippet.
 */

/** The single environment variable every snippet on every page reads. */
export const BASE_URL_ENV = "P9_BASE_URL";
export const API_KEY_ENV = "P9_API_KEY";

/** What `P9_BASE_URL` should be set to. The live gateway, from the build env. */
export const BASE_URL_VALUE = PUBLIC_API_BASE_URL;

const PROMPT = "Explain the P/9 router in one sentence.";

export interface Snippet {
  key: string;
  label: string;
  code: string;
}

export function CodeTabs({ tabs, className }: { tabs: Snippet[]; className?: string }) {
  const [active, setActive] = useState(tabs[0]?.key);
  const current = tabs.find((t) => t.key === active) ?? tabs[0];

  return (
    <div className={cn("overflow-hidden rounded-lg border border-border bg-card-2", className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border px-2 py-1.5">
        <div role="tablist" className="flex gap-0.5">
          {tabs.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={t.key === current.key}
              onClick={() => setActive(t.key)}
              className={cn(
                "rounded-md px-2.5 py-1 font-sans text-xs transition-colors",
                t.key === current.key
                  ? "bg-accent-soft text-foreground"
                  : "text-muted hover:text-foreground",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <CopyButton value={current.code} label="Copy" />
      </div>
      <pre className="overflow-x-auto p-4">
        <code className="font-mono text-[0.72rem] leading-relaxed text-foreground">
          {current.code}
        </code>
      </pre>
    </div>
  );
}

/** The two exports every snippet below assumes, as one copyable block. */
export const ENV_SETUP = `export ${BASE_URL_ENV}=${BASE_URL_VALUE}
export ${API_KEY_ENV}=your_key_here`;

/**
 * The shell preamble, shown once above a set of tabs.
 *
 * Its own copy button, because it is a separate paste from the snippet below
 * it: a reader sets the environment once per shell and then copies whichever
 * language tab they work in.
 */
export function EnvPreamble({ className }: { className?: string }) {
  return (
    <div className={cn("overflow-hidden rounded-lg border border-border bg-card-2", className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-1.5">
        <span className="font-sans text-xs text-muted">Set once, in your shell</span>
        <CopyButton value={ENV_SETUP} label="Copy" />
      </div>
      <pre className="overflow-x-auto px-4 py-3">
        <code className="font-mono text-[0.72rem] leading-relaxed text-foreground">
          {ENV_SETUP}
        </code>
      </pre>
    </div>
  );
}

/** Build OpenAI-compatible snippets for a specific catalogue model. */
export function modelSnippets(model: CatalogModel): Snippet[] {
  const id = model.id;

  if (model.kind === "embedding") {
    return [
      {
        key: "curl",
        label: "cURL",
        code: `curl "$${BASE_URL_ENV}/v1/embeddings" \\
  -H "Authorization: Bearer $${API_KEY_ENV}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${id}",
    "input": "The quick brown fox"
  }'`,
      },
      {
        key: "python",
        label: "Python",
        code: `import os
from openai import OpenAI

client = OpenAI(
    base_url=f"{os.environ['${BASE_URL_ENV}']}/v1",
    api_key=os.environ["${API_KEY_ENV}"],
)

resp = client.embeddings.create(
    model="${id}",
    input="The quick brown fox",
)
print(resp.data[0].embedding[:8])`,
      },
      {
        key: "node",
        label: "Node",
        code: `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: \`\${process.env.${BASE_URL_ENV}}/v1\`,
  apiKey: process.env.${API_KEY_ENV},
});

const resp = await client.embeddings.create({
  model: "${id}",
  input: "The quick brown fox",
});
console.log(resp.data[0].embedding.slice(0, 8));`,
      },
    ];
  }

  if (model.input === "image") {
    return imageSnippets(id);
  }

  if (model.kind === "decision") {
    return decisionSnippets(id);
  }

  return [
    {
      key: "curl",
      label: "cURL",
      code: `curl "$${BASE_URL_ENV}/v1/chat/completions" \\
  -H "Authorization: Bearer $${API_KEY_ENV}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${id}",
    "messages": [
      { "role": "user", "content": "${PROMPT}" }
    ]
  }'`,
    },
    {
      key: "python",
      label: "Python",
      code: `import os
from openai import OpenAI

client = OpenAI(
    base_url=f"{os.environ['${BASE_URL_ENV}']}/v1",
    api_key=os.environ["${API_KEY_ENV}"],
)

resp = client.chat.completions.create(
    model="${id}",
    messages=[{"role": "user", "content": "${PROMPT}"}],
)
print(resp.choices[0].message.content)`,
    },
    {
      key: "node",
      label: "Node",
      code: `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: \`\${process.env.${BASE_URL_ENV}}/v1\`,
  apiKey: process.env.${API_KEY_ENV},
});

const resp = await client.chat.completions.create({
  model: "${id}",
  messages: [{ role: "user", content: "${PROMPT}" }],
});
console.log(resp.choices[0].message.content);`,
    },
  ];
}

const DECISION_STATE = "My order #49204 never arrived and I want a refund right now.";

/**
 * A decision route (`paranine/laya-decision`, 2026-10-13): not chat, its own
 * endpoint, `POST /v1/systemone`, taking exactly `model`, `state` and
 * `questions`. The three examples are the team's handoff ones, run against the
 * gateway on 2026-10-13, with only the host read from `P9_BASE_URL`. Plain
 * `requests` and `fetch`, because the OpenAI SDKs have no method for this
 * endpoint (the docs guide shows their generic `post` call).
 */
function decisionSnippets(id: string): Snippet[] {
  return [
    {
      key: "curl",
      label: "cURL",
      code: `curl -sS "$${BASE_URL_ENV}/v1/systemone" \
  -H "Authorization: Bearer $${API_KEY_ENV}" \
  -H "Content-Type: application/json" \
  -d '{
    "model": "${id}",
    "state": "${DECISION_STATE}",
    "questions": {
      "q_intent": {
        "type": "choice",
        "instructions": "Classify the request.",
        "criteria": ["refund_request", "tracking_inquiry", "other"]
      }
    }
  }'`,
    },
    {
      key: "python",
      label: "Python",
      code: `import os
import requests

resp = requests.post(
    f"{os.environ['${BASE_URL_ENV}']}/v1/systemone",
    headers={"Authorization": f"Bearer {os.environ['${API_KEY_ENV}']}"},
    json={
        "model": "${id}",
        "state": "${DECISION_STATE}",
        "questions": {
            "q_intent": {
                "type": "choice",
                "instructions": "Classify the request.",
                "criteria": ["refund_request", "tracking_inquiry", "other"],
            },
        },
    },
    timeout=120,
)
resp.raise_for_status()
result = resp.json()

answer = result["answers"]["q_intent"]
print(answer["choice"], answer["probabilities"][answer["choice"]])
print(result["usage"])`,
    },
    {
      key: "node",
      label: "Node",
      code: `const resp = await fetch(\`\${process.env.${BASE_URL_ENV}}/v1/systemone\`, {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${process.env.${API_KEY_ENV}}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    model: "${id}",
    state: "${DECISION_STATE}",
    questions: {
      q_intent: {
        type: "choice",
        instructions: "Classify the request.",
        criteria: ["refund_request", "tracking_inquiry", "other"],
      },
    },
  }),
});
if (!resp.ok) throw new Error(\`\${resp.status}: \${await resp.text()}\`);
const result = await resp.json();

const answer = result.answers.q_intent;
console.log(answer.choice, answer.probabilities[answer.choice]);
console.log(result.usage);`,
    },
  ];
}

const OCR_PROMPT = "Convert the document to markdown.";

/**
 * Document OCR: the page image as a base64 data URL plus the one instruction,
 * read from `invoice.png` in the current folder. From the team's handoff,
 * whose examples were run against the gateway on 2026-10-07. `max_tokens`
 * stays at 4,096 because a request holds 8,192 tokens, image included.
 */
function imageSnippets(id: string): Snippet[] {
  return [
    {
      key: "curl",
      label: "cURL",
      code: `curl -sS "$${BASE_URL_ENV}/v1/chat/completions" \\
  -H "Authorization: Bearer $${API_KEY_ENV}" \\
  -H "Content-Type: application/json" \\
  --data-binary @- <<EOF
{
  "model": "${id}",
  "temperature": 0,
  "max_tokens": 4096,
  "messages": [{
    "role": "user",
    "content": [
      {"type": "image_url", "image_url": {"url": "data:image/png;base64,$(base64 < invoice.png | tr -d '\\n')"}},
      {"type": "text", "text": "${OCR_PROMPT}"}
    ]
  }]
}
EOF`,
    },
    {
      key: "python",
      label: "Python",
      code: `import base64
import os
from openai import OpenAI

client = OpenAI(
    base_url=f"{os.environ['${BASE_URL_ENV}']}/v1",
    api_key=os.environ["${API_KEY_ENV}"],
)

with open("invoice.png", "rb") as f:
    image_b64 = base64.b64encode(f.read()).decode()

resp = client.chat.completions.create(
    model="${id}",
    temperature=0,
    max_tokens=4096,
    messages=[{
        "role": "user",
        "content": [
            {"type": "image_url", "image_url": {"url": f"data:image/png;base64,{image_b64}"}},
            {"type": "text", "text": "${OCR_PROMPT}"},
        ],
    }],
)
print(resp.choices[0].message.content)`,
    },
    {
      key: "node",
      label: "Node",
      code: `import fs from "node:fs";
import OpenAI from "openai";

const client = new OpenAI({
  baseURL: \`\${process.env.${BASE_URL_ENV}}/v1\`,
  apiKey: process.env.${API_KEY_ENV},
});

const imageB64 = fs.readFileSync("invoice.png").toString("base64");

const resp = await client.chat.completions.create({
  model: "${id}",
  temperature: 0,
  max_tokens: 4096,
  messages: [{
    role: "user",
    content: [
      { type: "image_url", image_url: { url: \`data:image/png;base64,\${imageB64}\` } },
      { type: "text", text: "${OCR_PROMPT}" },
    ],
  }],
});
console.log(resp.choices[0].message.content);`,
    },
  ];
}

/**
 * Anthropic's native Messages API, through P/9.
 *
 * The same signature as every other call on the platform (the P/9 key, the P/9
 * host in the base URL) plus one header, `anthropic-version`, which
 * Anthropic's own API requires and which the gateway passes through. The model
 * id is Anthropic's, not a P/9 route alias: this path resolves against the
 * provider, so the catalogue does not name it.
 *
 * `base_url` here is the host root because the Anthropic SDKs append
 * `/v1/messages` themselves; the cURL tab writes that path out in full.
 *
 * **The tabs carry the key in different headers, and both are correct.** cURL
 * uses `Authorization: Bearer`, the header every other P/9 call uses. The SDKs
 * send it as `x-api-key`, because that is the only thing Anthropic's clients
 * do with `api_key` and they offer no way to change it. Do not "fix" either
 * one to match the other.
 */
export function anthropicSnippets(model = "claude-sonnet-5"): Snippet[] {
  return [
    {
      key: "curl",
      label: "cURL",
      code: `curl "$${BASE_URL_ENV}/v1/messages" \\
  -H "Authorization: Bearer $${API_KEY_ENV}" \\
  -H "anthropic-version: 2023-06-01" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${model}",
    "max_tokens": 200,
    "messages": [
      { "role": "user", "content": "Hello" }
    ],
    "stream": true
  }'`,
    },
    {
      key: "python",
      label: "Python",
      code: `import os
import anthropic

client = anthropic.Anthropic(
    # The host root, not the /v1 root: the SDK appends /v1/messages itself.
    base_url=os.environ["${BASE_URL_ENV}"],
    api_key=os.environ["${API_KEY_ENV}"],
)

with client.messages.stream(
    model="${model}",
    max_tokens=200,
    messages=[{"role": "user", "content": "Hello"}],
) as stream:
    for text in stream.text_stream:
        print(text, end="", flush=True)`,
    },
    {
      key: "node",
      label: "Node",
      code: `import Anthropic from "@anthropic-ai/sdk";

const client = new Anthropic({
  // The host root, not the /v1 root: the SDK appends /v1/messages itself.
  baseURL: process.env.${BASE_URL_ENV},
  apiKey: process.env.${API_KEY_ENV},
});

const stream = client.messages.stream({
  model: "${model}",
  max_tokens: 200,
  messages: [{ role: "user", content: "Hello" }],
});

stream.on("text", (text) => process.stdout.write(text));
await stream.finalMessage();`,
    },
  ];
}

/** The drawer's Code tab: snippets plus where the key in them comes from. */
export function ModelCodeSnippets({ model }: { model: CatalogModel }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="font-sans text-sm leading-relaxed text-muted">
        {model.name} is OpenAI-compatible. Point any OpenAI SDK at the P/9 base URL and set your key
        as <code className="font-mono text-xs text-foreground">{API_KEY_ENV}</code>.
      </p>
      <EnvPreamble />
      <CodeTabs tabs={modelSnippets(model)} />
      <p className="font-sans text-xs leading-relaxed text-muted-2">
        Create a key on the{" "}
        <Link
          href="/dashboard/keys"
          className="text-foreground underline decoration-border-strong underline-offset-4 hover:decoration-foreground"
        >
          API keys page
        </Link>
        . Both variables above are read from your environment, so the same snippet works in every
        shell you export them in.
      </p>
    </div>
  );
}
